"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import type { DepositMethod } from "@/types";
import {
  formatPriceSimple,
  type SupportedCurrency,
} from "@/lib/utils/currency";

interface DepositNoticeProps {
  methods: DepositMethod[] | undefined;
  depositAmount?: number | null;
  sourceCurrency: SupportedCurrency;
  className?: string;
}

/** 안내 문구의 원문자 순서는 admin 설정 순서(cash → passport → card)와 고정 일치 */
const METHOD_ORDER: DepositMethod[] = ["cash", "passport", "card"];
const CIRCLED_NUMBERS = ["①", "②", "③"];

/**
 * 보증금 안내 (안내 전용 — 온라인 청구 없음)
 *
 * 파트너가 설정한 방식만 원문자 번호로 나열한다. 형식(확정 목업):
 *   * 픽업 시 보증금 필요
 *   ① 현금 맡기기 ₩50,000 ② 여권 보관 ③ 카드 결제
 *
 * PC는 결제 버튼들 하단, 모바일은 접힌 바·펼친 드로어의 버튼 바로 위에 표시.
 */
export default function DepositNotice({
  methods,
  depositAmount,
  sourceCurrency,
  className,
}: DepositNoticeProps) {
  const t = useTranslations("product.booking.deposit");

  const orderedMethods = METHOD_ORDER.filter((m) => methods?.includes(m));
  if (orderedMethods.length === 0) return null;

  return (
    <div
      className={cn(
        "text-left text-xs leading-relaxed text-red-600",
        className,
      )}
    >
      <p className="font-bold">{t("title")}</p>
      <p>
        {orderedMethods.map((method, index) => (
          <span key={method} className="mr-1.5 whitespace-nowrap">
            {CIRCLED_NUMBERS[index]} {t(`methods.${method}`)}
            {method === "cash" && depositAmount && depositAmount > 0
              ? ` ${formatPriceSimple(depositAmount, sourceCurrency)}`
              : ""}
          </span>
        ))}
      </p>
    </div>
  );
}
